import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { parseStringToDayjs } from '@/utils/format';

type Range = { start?: string; end?: string };

type Props = {
  start?: string;
  end?: string;
  max?: string;
  /** which='start'|'end', value='' 이면 지우기 */
  onChange: (which: 'start' | 'end', value: string) => void;
};

/** 숫자만 뽑아 YYYY-MM-DD 자리에 맞춰 하이픈을 넣는다 */
const maskDate = (digits: string) =>
  [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)]
    .filter((part) => part.length > 0)
    .join('-');

/** 숫자 n 개를 지난 지점의 커서 위치(하이픈은 건너뛴다) */
const caretAfterDigits = (masked: string, digitCount: number) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < masked.length; i += 1) {
    if (/\d/.test(masked[i])) {
      seen += 1;
      if (seen === digitCount) return i + 1;
    }
  }
  return masked.length;
};

/**
 * 기간 필터의 시작~종료 날짜 입력 (전자결재/회의록 공용).
 * 시안: 흰 박스 + radius 4, 값 있으면 검정 테두리. 날짜는 YYYY-MM-DD 로 표기.
 * 네이티브 date 입력은 로케일 표기(2025. 07. 01.)라 숨기고 달력(showPicker) 용으로만 쓴다.
 * 표시 겸 입력은 텍스트 인풋이라 달력 없이 키보드로 YYYY-MM-DD 를 직접 칠 수도 있다.
 */
const DateRangePicker = ({ start, end, max, onChange }: Props) => {
  const { t } = useTranslation();
  // 입력한 날짜는 화면에 그대로 두되, 조회 조건을 만족할 때만 상위 필터에 올린다
  const [draft, setDraft] = useState<Range>({ start, end });

  useEffect(() => {
    setDraft({ start, end });
  }, [start, end]);

  /** 조회할 수 없는 조합이면 사유를 돌려준다. 과거 날짜여도 간격이 1년 이내면 문제 없다 */
  const getGuide = ({ start: from, end: to }: Range) => {
    if (from && to && from > to) return t('시작일이 종료일보다 늦어요.');
    if ([from, to].some((date) => date && max && date > max))
      return t('오늘 이후 날짜는 조회할 수 없어요.');
    const fromDate = parseStringToDayjs(from);
    const toDate = parseStringToDayjs(to);
    if (fromDate && toDate && toDate.isAfter(fromDate.add(1, 'year')))
      return t('최대 1년치까지만 조회할 수 있어요.');
    return null;
  };

  // 여러 조건에 걸려도 먼저 고쳐야 할 것 하나만 보여준다
  const guide = getGuide(draft);

  /** 조건을 만족하는 순간에만 반영 — 보류해 둔 반대쪽 값도 이때 함께 올린다 */
  const apply = (which: 'start' | 'end', value: string) => {
    const next: Range =
      which === 'start' ? { ...draft, start: value } : { ...draft, end: value };
    setDraft(next);
    if (getGuide(next)) return;
    if (next.start !== start) onChange('start', next.start ?? '');
    if (next.end !== end) onChange('end', next.end ?? '');
  };

  return (
    <>
      <DateRow>
        <DateBox
          value={draft.start}
          label={t('시작일')}
          max={max}
          onPick={(v) => apply('start', v)}
          onClear={() => apply('start', '')}
        />
        <Tilde>~</Tilde>
        <DateBox
          value={draft.end}
          label={t('종료일')}
          max={max}
          onPick={(v) => apply('end', v)}
          onClear={() => apply('end', '')}
        />
      </DateRow>
      {guide && <RangeGuide>{guide}</RangeGuide>}
    </>
  );
};

export default DateRangePicker;

type BoxProps = {
  value?: string;
  /** 스크린리더에서 시작/종료를 구분할 이름 */
  label: string;
  max?: string;
  /** 입력이 끝난 날짜(YYYY-MM-DD). 값을 대신 바꾸지 않고 그대로 올린다 */
  onPick: (value: string) => void;
  onClear: () => void;
};

const DateBox = ({ value, label, max, onPick, onClear }: BoxProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  // 타이핑 중간값(YYYY-MM 처럼 미완성 상태)은 로컬로만 들고, 완성됐을 때만 상위로 올린다
  const [typed, setTyped] = useState(value ?? '');
  // 마스킹 후 되돌릴 커서 위치. 렌더가 끝난 뒤 적용해야 값 갱신에 밀리지 않는다
  const caretRef = useRef<number | null>(null);

  useEffect(() => {
    setTyped(value ?? '');
  }, [value]);

  useLayoutEffect(() => {
    if (caretRef.current === null || !textRef.current) return;
    const pos = caretRef.current;
    caretRef.current = null;
    textRef.current.setSelectionRange(pos, pos);
  }, [typed]);

  const open = () => {
    const el = ref.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (el?.showPicker) el.showPicker();
    else el?.focus();
  };

  /** 입력값이 완성된 날짜면 반영, 비었으면 해제, 미완성이면 되돌린다 */
  const commit = (next: string) => {
    if (!next) {
      onClear();
      return;
    }
    const parsed = parseStringToDayjs(next);
    if (parsed) {
      onPick(next);
    } else {
      setTyped(value ?? '');
    }
  };

  return (
    <DateField focused={!!value}>
      <DateInput
        ref={textRef}
        value={typed}
        placeholder={'YYYY-MM-DD'}
        inputMode={'numeric'}
        aria-label={label}
        onFocus={(e) => {
          // 클릭으로 커서가 놓인 뒤에 전체 선택해야 선택이 유지된다
          // (mouseup 을 preventDefault 하면 팝오버가 닫혀버려 이 방식을 쓴다)
          const input = e.target;
          requestAnimationFrame(() => input.select());
        }}
        onChange={(e) => {
          const input = e.target;
          const caret = input.selectionStart ?? input.value.length;
          // 커서 앞 숫자 개수를 세어 두고, 마스킹 후 같은 자리로 되돌린다
          // (안 그러면 가운데 한 글자만 고칠 때 커서가 끝으로 튄다)
          const digitsBeforeCaret = input.value
            .slice(0, caret)
            .replace(/\D/g, '').length;
          const next = maskDate(input.value.replace(/\D/g, '').slice(0, 8));
          caretRef.current = caretAfterDigits(next, digitsBeforeCaret);
          setTyped(next);
          if (next.length === 10) commit(next);
        }}
        onBlur={() => commit(typed)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(typed);
          }
        }}
      />
      {value && (
        <IconButton
          type={'button'}
          aria-label={t('{{label}} 지우기', { label })}
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          <ClearIcon />
        </IconButton>
      )}
      <IconButton
        type={'button'}
        aria-label={t('{{label}} 달력 열기', { label })}
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
      >
        <CalendarIcon />
      </IconButton>
      <HiddenInput
        ref={ref}
        type={'date'}
        value={value ?? ''}
        max={max}
        onChange={(e) => onPick(e.target.value)}
      />
    </DateField>
  );
};

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RangeGuide = styled.div`
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  color: var(--dk-danger, #f55060);
`;

const Tilde = styled.span`
  width: 12px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--dk-text, black);
  flex-shrink: 0;
`;

const DateField = styled.div<{ focused: boolean }>`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px 14px 16px;
  background: var(--dk-bg, #ffffff);
  border: 1px solid ${({ focused }) => (focused ? 'black' : '#e7e9ec')};
  border-radius: 4px;
  &:focus-within {
    border-color: black;
  }
`;

const DateInput = styled.input`
  flex: 1 1 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
  &::placeholder {
    color: var(--dk-text-dim, #b7bbc2);
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  background: transparent;
  cursor: pointer;
`;

/** 네이티브 date 입력 — showPicker 용으로만 존재(화면에는 숨김) */
const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
`;

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#D4D7DB" />
    <path
      d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <g clipPath="url(#dateRangeCalClip)">
      <path
        d="M7.23975 3.46436L7.23975 5.82763"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.7598 3.46436L12.7598 5.82763"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.58398 7.52588V8.02588C4.30784 8.02588 4.08398 7.80202 4.08398 7.52588H4.58398ZM15.4156 7.52588H15.9156C15.9156 7.80202 15.6917 8.02588 15.4156 8.02588V7.52588ZM15.4156 4.69156V4.19156C15.6917 4.19156 15.9156 4.41542 15.9156 4.69156H15.4156ZM4.58398 4.69156H4.08398C4.08398 4.41542 4.30784 4.19156 4.58398 4.19156V4.69156ZM4.58398 7.52588V7.02588H15.4156V7.52588V8.02588H4.58398V7.52588ZM15.4156 7.52588H14.9156V4.69156H15.4156H15.9156V7.52588H15.4156ZM15.4156 4.69156V5.19156H4.58398V4.69156V4.19156H15.4156V4.69156ZM4.58398 4.69156H5.08398V7.52588H4.58398H4.08398V4.69156H4.58398Z"
        fill="black"
      />
      <path
        d="M4.58398 7.52588V7.02588C4.30784 7.02588 4.08398 7.24974 4.08398 7.52588H4.58398ZM15.4156 7.52588H15.9156C15.9156 7.24974 15.6917 7.02588 15.4156 7.02588V7.52588ZM15.4156 15.8133V16.3133C15.6917 16.3133 15.9156 16.0895 15.9156 15.8133H15.4156ZM4.58398 15.8133H4.08398C4.08398 16.0895 4.30784 16.3133 4.58398 16.3133V15.8133ZM4.58398 7.52588V8.02588H15.4156V7.52588V7.02588H4.58398V7.52588ZM15.4156 7.52588H14.9156V15.8133H15.4156H15.9156V7.52588H15.4156ZM15.4156 15.8133V15.3133H4.58398V15.8133V16.3133H15.4156V15.8133ZM4.58398 15.8133H5.08398V7.52588H4.58398H4.08398V15.8133H4.58398Z"
        fill="black"
      />
    </g>
    <defs>
      <clipPath id="dateRangeCalClip">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
